// src/Agreements.js
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  IconButton,
  FormControlLabel,
  Checkbox,
  Paper,
  TableContainer,
  Divider,
  Chip,
  Tooltip,
  Snackbar,
  Alert,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/system';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import jsPDF from 'jspdf';

import { useAuth } from './AuthContext';
import { useAccess } from './useAccess'; // ✅ keep preview/locked system intact
import { getAllItems, addItem, updateItem, deleteItem, loadUserSingleDoc } from './firestore';

const PrimaryButton = styled(Button)({
  borderRadius: 12,
  padding: '10px 16px',
  textTransform: 'none',
  fontWeight: 800,
  boxShadow: 'none',
  backgroundColor: '#000',
  color: '#fff',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
  '&:hover': {
    backgroundColor: '#222',
    transform: 'translateY(-1px)',
    boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
  },
  '&:disabled': { backgroundColor: '#111', color: 'rgba(255,255,255,0.6)' },
});

const SubtleButton = styled(Button)({
  borderRadius: 12,
  padding: '10px 16px',
  textTransform: 'none',
  fontWeight: 800,
  boxShadow: 'none',
  backgroundColor: '#fff',
  color: '#000',
  border: '1px solid rgba(0,0,0,0.15)',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
  '&:hover': {
    backgroundColor: '#fafafa',
    transform: 'translateY(-1px)',
    boxShadow: '0 10px 24px rgba(0,0,0,0.08)',
  },
});

const SoftCard = ({ title, icon, right, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, sm: 2.5 },
      borderRadius: 4,
      border: '1px solid rgba(0,0,0,0.08)',
      bgcolor: 'white',
      boxShadow: '0 18px 45px rgba(0,0,0,0.05)',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 999,
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'rgba(0,0,0,0.06)',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 220, flex: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 900 }}>
          {title}
        </Typography>
      </Box>
      {right}
    </Box>

    <Divider sx={{ my: 2 }} />

    {children}
  </Paper>
);

// ✅ consistent white dropdown menus
const menuProps = {
  PaperProps: {
    sx: {
      bgcolor: '#ffffff',
      color: '#000000',
      '& .MuiMenuItem-root': {
        color: '#000000',
        '&:hover': { backgroundColor: '#f5f5f5' },
        '&.Mui-selected': { backgroundColor: '#e0e0e0', '&:hover': { backgroundColor: '#d0d0d0' } },
      },
    },
  },
};

// ✅ Template WITHOUT signatures (PDF adds them once at the end)
const ndisAgreementTemplate = (
  participantName,
  ndisNumber,
  startDate,
  endDate,
  providerName,
  providerAbn,
  providerAddress,
  providerContact
) => `
NDIS Service Agreement

Parties
This Service Agreement is made on ${startDate} between:
Provider: ${providerName}, ABN ${providerAbn}, Address: ${providerAddress}
Participant: ${participantName}, NDIS Participant Number: ${ndisNumber}

Purpose
This Agreement outlines the disability support services provided by ${providerName} to ${participantName} under the National Disability Insurance Scheme (NDIS). It supports the Participant’s NDIS goals, independence, choice, and social participation.

Services Provided
${providerName} will provide NDIS support services tailored to the Participant’s plan and goals, including:
• Personal Care and Daily Living: Assistance with hygiene, meal preparation, and household tasks.
• Community Participation: Support for social, recreational, or community activities.
• Skill Development: Support to build independence and everyday life skills.
• Transport Assistance: Transport to appointments or community activities as agreed.

Service Rates and Schedule
Rates align with the NDIS Price Guide limits (as applicable). Services will be provided as agreed between the Provider and Participant based on the Participant’s plan and availability. A schedule may be confirmed in writing (email or SMS) and adjusted with mutual consent.

Responsibilities of the Provider
The Provider agrees to:
• Deliver services in accordance with the NDIS Code of Conduct.
• Maintain appropriate insurance (public liability and professional indemnity).
• Invoice accurately with service dates, types, hours, and any approved travel.
• Communicate respectfully and give reasonable notice of changes/cancellations where possible.
• Keep appropriate records in line with NDIS requirements.

Responsibilities of the Participant
The Participant agrees to:
• Communicate support needs and changes promptly.
• Treat the Provider with courtesy and respect.
• Provide a safe environment for supports to occur.
• Provide reasonable notice for cancellations where possible.
• Inform the Provider of any changes to the NDIS plan.

Payment Terms
Invoices will be issued to the Participant’s plan manager (if applicable) and paid under the plan management arrangements.

Cancellation Policy
If the Participant cancels with less than 1 business day’s notice, the Provider may charge the agreed support fee in line with NDIS guidelines.

Changes to This Agreement
Any changes must be agreed in writing by both parties.

Ending This Agreement
This Agreement begins on ${startDate} and ${endDate ? `ends on ${endDate}` : 'remains ongoing until terminated'}.
Either party may terminate with reasonable notice.

Feedback and Complaints
Contact the Provider at ${providerContact} for feedback or complaints. The Provider will handle complaints promptly and respectfully.

Privacy and Confidentiality
The Provider complies with Australian Privacy Principles and NDIS privacy standards.

Contact Details
Provider: ${providerName}
Phone/Email: ${providerContact}
`.trim();

// --- PDF Signature Block Helper ---
const addSignatureBlock = (doc, startY, agreementStartDate) => {
  const margin = 20;
  const pageWidth = doc.internal.pageSize.getWidth();
  const usableWidth = pageWidth - margin * 2;

  const leftColX = margin;
  const rightColX = margin + usableWidth / 2 + 5;
  const line = '______________________________';
  let y = startY;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('Signatures', margin, y);
  y += 10;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);

  doc.text('Provider', leftColX, y);
  doc.text('Participant', rightColX, y);
  y += 8;

  doc.text(`Name (print): ${line}`, leftColX, y);
  doc.text(`Name (print): ${line}`, rightColX, y);
  y += 10;

  doc.text(`Signature:     ${line}`, leftColX, y);
  doc.text(`Signature:     ${line}`, rightColX, y);
  y += 10;

  doc.text(`Date: ${agreementStartDate || ''}`, leftColX, y);
  doc.text(`Date: ${agreementStartDate || ''}`, rightColX, y);

  y += 12;
  doc.setFontSize(9);
  doc.setTextColor(90);
  doc.text('By signing, both parties agree to the terms of this Service Agreement.', margin, y);
  doc.setTextColor(0);
};

const Agreements = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // ✅ preview/locked access
  const access = useAccess?.() || {};
  const isLocked =
    !!access.locked ||
    !!access.isLocked ||
    !!access.previewLocked ||
    !!access.isPreviewLocked ||
    !!access.actionsLocked ||
    false;

  const openPaywall =
    access.openPaywall ||
    access.startTrial ||
    access.goToBilling ||
    access.requireUpgrade ||
    access.promptUpgrade ||
    null;

  const [agreements, setAgreements] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [userInfo, setUserInfo] = useState({});

  const [participantId, setParticipantId] = useState('');
  const [details, setDetails] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isOngoing, setIsOngoing] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState('');

  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const showToast = (severity, message) => setToast({ open: true, severity, message });

  const runIfUnlocked = (fn, msg = 'Preview mode — actions are locked. Start a free trial to use this feature.') => {
    if (isLocked) {
      showToast('info', msg);
      if (typeof openPaywall === 'function') openPaywall();
      return;
    }
    fn();
  };

  useEffect(() => {
    if (!currentUser) return;

    const loadData = async () => {
      try {
        const [loadedAgreements, loadedParticipants, loadedUserInfo] = await Promise.all([
          getAllItems(currentUser.uid, 'agreements'),
          getAllItems(currentUser.uid, 'participants'),
          loadUserSingleDoc(currentUser.uid, 'userInfo', {}),
        ]);

        setAgreements(loadedAgreements || []);
        setParticipants(loadedParticipants || []);
        setUserInfo(loadedUserInfo || {});
      } catch (error) {
        console.error('Error loading data:', error);
        showToast('error', 'Failed to load agreements or participants.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser]);

  const participantsById = useMemo(() => {
    const map = new Map();
    (participants || []).forEach((p) => map.set(p.id, p));
    return map;
  }, [participants]);

  const providerDetails = useMemo(() => {
    return {
      name: userInfo.name || 'Your Provider Name',
      abn: userInfo.abn || 'Your ABN',
      address: userInfo.address || 'Your Address',
      contact: userInfo.contact || userInfo.email || 'Your Phone/Email',
    };
  }, [userInfo]);

  const selectedParticipant = participantsById.get(participantId) || null;

  const filteredAgreements = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agreements || [];

    return (agreements || []).filter((a) => {
      const p = participantsById.get(a.participantId);
      const name = (p?.name || '').toLowerCase();
      const ndis = (p?.ndisNumber || '').toLowerCase();
      const s = String(a.startDate || '').toLowerCase();
      const e = String(a.endDate || 'ongoing').toLowerCase();
      const body = String(a.details || '').toLowerCase();

      return name.includes(q) || ndis.includes(q) || s.includes(q) || e.includes(q) || body.includes(q);
    });
  }, [agreements, participantsById, query]);

  const handleGenerate = () => {
    runIfUnlocked(() => {
      if (!participantId) return showToast('warning', 'Please select a participant first.');
      if (!startDate) return showToast('warning', 'Please select a start date.');
      if (!isOngoing && !endDate) return showToast('warning', 'Please select an end date or mark as ongoing.');
      if (!isOngoing && new Date(startDate) > new Date(endDate))
        return showToast('warning', 'Start date must be before end date.');

      const participant = selectedParticipant || { name: '', ndisNumber: '' };

      const template = ndisAgreementTemplate(
        participant.name,
        participant.ndisNumber,
        startDate,
        isOngoing ? null : endDate,
        providerDetails.name,
        providerDetails.abn,
        providerDetails.address,
        providerDetails.contact
      );

      setDetails(template);
      setShowForm(true);
      setEditingId(null);
      showToast('success', 'Template generated. Review and save.');
    }, 'Preview mode — actions are locked. Start a free trial to generate agreements.');
  };

  const resetForm = () => {
    setParticipantId('');
    setDetails('');
    setStartDate('');
    setEndDate('');
    setIsOngoing(false);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (isLocked) {
      showToast('info', 'Preview mode — actions are locked. Start a free trial to save agreements.');
      if (typeof openPaywall === 'function') openPaywall();
      return;
    }

    if (!participantId) return showToast('warning', 'Select a participant.');
    if (!details.trim()) return showToast('warning', 'Agreement text is required.');
    if (!startDate) return showToast('warning', 'Start date is required.');
    if (!isOngoing && !endDate) return showToast('warning', 'Select an end date or mark as ongoing.');
    if (!isOngoing && new Date(startDate) > new Date(endDate))
      return showToast('warning', 'Start date must be before end date.');

    const agreementData = {
      participantId,
      details: details.trim(),
      startDate,
      endDate: isOngoing ? null : endDate,
      updatedAt: new Date().toISOString(),
    };

    try {
      if (editingId) {
        await updateItem(currentUser.uid, 'agreements', editingId, agreementData);
        setAgreements((prev) => (prev || []).map((a) => (a.id === editingId ? { ...a, ...agreementData } : a)));
        showToast('success', 'Agreement updated.');
      } else {
        const newAgreement = await addItem(currentUser.uid, 'agreements', agreementData);
        setAgreements((prev) => [...(prev || []), newAgreement]);
        showToast('success', 'Agreement saved.');
      }

      resetForm();
    } catch (error) {
      console.error('Save failed:', error);
      showToast('error', `Failed to save agreement: ${error.message}`);
    }
  };

  const handleEdit = (agreement) => {
    runIfUnlocked(() => {
      setParticipantId(agreement.participantId);
      setDetails(agreement.details || '');
      setStartDate(agreement.startDate || '');
      setEndDate(agreement.endDate || '');
      setIsOngoing(!agreement.endDate);
      setEditingId(agreement.id);
      setShowForm(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 'Preview mode — actions are locked. Start a free trial to edit agreements.');
  };

  const handleDelete = async (id) => {
    if (isLocked) {
      showToast('info', 'Preview mode — actions are locked. Start a free trial to delete agreements.');
      if (typeof openPaywall === 'function') openPaywall();
      return;
    }

    if (!window.confirm('Permanently delete this service agreement?')) return;

    try {
      await deleteItem(currentUser.uid, 'agreements', id);
      setAgreements((prev) => (prev || []).filter((a) => a.id !== id));
      showToast('success', 'Agreement deleted.');
    } catch (error) {
      showToast('error', `Delete failed: ${error.message}`);
    }
  };

  // ✅ PDF is allowed even in preview mode
  const handleDownload = (agreement) => {
    try {
      const doc = new jsPDF({ format: 'a4', unit: 'mm' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 18;

      // Header bar
      doc.setFillColor(0, 0, 0);
      doc.rect(0, 0, pageWidth, 18, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('NDIS Service Agreement', margin, 12);

      // Meta line
      doc.setTextColor(0);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);

      const participant = participantsById.get(agreement.participantId) || { name: 'Unknown' };
      const meta = `Participant: ${participant.name}   •   Start: ${agreement.startDate}   •   End: ${
        agreement.endDate || 'Ongoing'
      }`;
      doc.text(meta, margin, 26);

      // Body
      doc.setFontSize(11);
      let y = 36;

      const lines = doc.splitTextToSize(String(agreement.details || ''), pageWidth - 2 * margin);
      lines.forEach((line) => {
        if (y > pageHeight - 26) {
          doc.addPage();
          // repeat header
          doc.setFillColor(0, 0, 0);
          doc.rect(0, 0, pageWidth, 18, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(14);
          doc.text('NDIS Service Agreement', margin, 12);
          doc.setTextColor(0);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(11);
          y = 28;
        }
        doc.text(line, margin, y);
        y += 6.2;
      });

      // Signatures (avoid cut off)
      if (y > pageHeight - 70) {
        doc.addPage();
        doc.setFillColor(0, 0, 0);
        doc.rect(0, 0, pageWidth, 18, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text('NDIS Service Agreement', margin, 12);
        doc.setTextColor(0);
        y = 28;
      } else {
        y += 8;
      }

      addSignatureBlock(doc, y, agreement.startDate);

      const safeName = String(participant.name || 'participant').trim().replace(/\s+/g, '-');
      doc.save(`service-agreement-${safeName}-${agreement.id}.pdf`);
    } catch (error) {
      console.error('PDF error:', error);
      showToast('error', 'Failed to generate PDF.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>Loading agreements...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '1200px', mx: 'auto' }}>
      {/* Premium header */}
      <Box
        sx={{
          mb: 2.5,
          p: 2.5,
          borderRadius: 4,
          border: '1px solid rgba(0,0,0,0.08)',
          background:
            'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(245,247,255,1) 45%, rgba(245,255,248,1) 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <SubtleButton onClick={() => navigate('/')} startIcon={<ArrowBackIcon />}>
            Back
          </SubtleButton>

          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                Service Agreements
              </Typography>

              {isLocked && (
                <Chip
                  label="Preview mode — actions locked"
                  sx={{ borderRadius: 999, fontWeight: 900, bgcolor: 'rgba(0,0,0,0.08)' }}
                />
              )}
            </Box>

            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)', mt: 0.5 }}>
              Generate, edit, and download NDIS-style service agreements as PDFs.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
              <Chip
                size="small"
                label={`Agreements: ${(agreements || []).length}`}
                sx={{ borderRadius: 999, fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)' }}
              />
              <Chip
                size="small"
                label={`Provider: ${providerDetails.name}`}
                sx={{ borderRadius: 999, fontWeight: 800, bgcolor: 'rgba(0,0,0,0.06)' }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Create / edit */}
      <SoftCard
        title={editingId ? 'Edit agreement' : 'Create agreement'}
        icon={<DescriptionOutlinedIcon fontSize="small" />}
        right={showForm ? <SubtleButton onClick={resetForm}>Close</SubtleButton> : null}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <Select
            value={participantId}
            onChange={(e) => {
              setParticipantId(e.target.value);
              if (!editingId) setShowForm(false);
            }}
            displayEmpty
            sx={{ minWidth: 280 }}
            MenuProps={menuProps}
            disabled={isLocked}
          >
            <MenuItem value="">
              <em>Select participant</em>
            </MenuItem>
            {(participants || [])
              .filter((p) => !p.archived)
              .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
              .map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
          </Select>

          <TextField
            label="Start date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 200 }}
            disabled={isLocked}
          />

          {!isOngoing && (
            <TextField
              label="End date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 200 }}
              disabled={isLocked}
            />
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={isOngoing}
                onChange={(e) => {
                  if (isLocked) {
                    showToast('info', 'Preview mode — actions are locked. Start a free trial to edit agreements.');
                    if (typeof openPaywall === 'function') openPaywall();
                    return;
                  }
                  setIsOngoing(e.target.checked);
                  if (e.target.checked) setEndDate('');
                }}
                disabled={isLocked}
              />
            }
            label="Ongoing"
          />

          {!showForm ? (
            <PrimaryButton
              startIcon={<AddIcon />}
              onClick={handleGenerate}
              disabled={isLocked || !participantId || !startDate || (!isOngoing && !endDate)}
            >
              Generate template
            </PrimaryButton>
          ) : (
            <PrimaryButton
              startIcon={<EditIcon />}
              onClick={handleSave}
              disabled={isLocked || !participantId || !startDate || !details.trim() || (!isOngoing && !endDate)}
            >
              {editingId ? 'Save changes' : 'Save agreement'}
            </PrimaryButton>
          )}
        </Box>

        {participantId && selectedParticipant && (
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              size="small"
              icon={<PersonOutlineIcon />}
              label={`${selectedParticipant.name} • NDIS ${selectedParticipant.ndisNumber || '—'}`}
              sx={{ borderRadius: 999, fontWeight: 800, bgcolor: 'rgba(0,0,0,0.06)' }}
            />
            <Chip
              size="small"
              label={isOngoing ? `Start ${startDate} • Ongoing` : `Start ${startDate} • End ${endDate || '—'}`}
              sx={{ borderRadius: 999, fontWeight: 800, bgcolor: 'rgba(0,0,0,0.06)' }}
            />
          </Box>
        )}

        {showForm && (
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Agreement text (edit as needed)"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              multiline
              minRows={18}
              fullWidth
              disabled={isLocked}
              helperText="Review wording before saving. Signatures will be added to the PDF automatically."
            />

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
              <PrimaryButton
                startIcon={<EditIcon />}
                onClick={handleSave}
                disabled={isLocked || !participantId || !startDate || !details.trim() || (!isOngoing && !endDate)}
              >
                {editingId ? 'Save changes' : 'Save agreement'}
              </PrimaryButton>

              <SubtleButton
                onClick={() => {
                  const previewAgreement = {
                    id: editingId || 'preview',
                    participantId,
                    details,
                    startDate,
                    endDate: isOngoing ? null : endDate,
                  };
                  handleDownload(previewAgreement);
                }}
                startIcon={<DownloadIcon />}
                disabled={!details.trim() || !startDate || (!isOngoing && !endDate)}
              >
                Download PDF (preview)
              </SubtleButton>

              <SubtleButton onClick={resetForm}>Cancel</SubtleButton>
            </Box>
          </Box>
        )}
      </SoftCard>

      {/* List */}
      <Paper
        elevation={0}
        sx={{
          mt: 2.5,
          p: 2,
          borderRadius: 4,
          border: '1px solid rgba(0,0,0,0.08)',
          bgcolor: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            Saved agreements
          </Typography>
          <Box sx={{ flex: 1 }} />

          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search participant / NDIS / dates / text…"
            sx={{ minWidth: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: 'rgba(0,0,0,0.03)',
                  '& th': { fontWeight: 900, color: 'rgba(0,0,0,0.75)' },
                }}
              >
                <TableCell>Participant</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>End</TableCell>
                <TableCell>Preview</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredAgreements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <Typography sx={{ fontWeight: 900, mb: 0.5 }}>No agreements found</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)' }}>
                      Create one above, or adjust your search.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAgreements
                  .slice()
                  .sort((a, b) => String(b.startDate || '').localeCompare(String(a.startDate || '')))
                  .map((a) => {
                    const p = participantsById.get(a.participantId);
                    const name = p?.name || 'Unknown';
                    const preview = String(a.details || '').replace(/\s+/g, ' ').slice(0, 90);

                    return (
                      <TableRow key={a.id} hover>
                        <TableCell sx={{ fontWeight: 800 }}>{name}</TableCell>
                        <TableCell>{a.startDate || '—'}</TableCell>
                        <TableCell>{a.endDate || 'Ongoing'}</TableCell>
                        <TableCell sx={{ color: 'rgba(0,0,0,0.65)' }}>{preview}{preview ? '…' : ''}</TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title={isLocked ? 'Preview mode (locked)' : 'Edit'}>
                            <span>
                              <IconButton size="small" onClick={() => handleEdit(a)} disabled={isLocked}>
                                <EditIcon />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip title={isLocked ? 'Preview mode (locked)' : 'Delete'}>
                            <span>
                              <IconButton size="small" onClick={() => handleDelete(a.id)} color="error" disabled={isLocked}>
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip title="Download PDF">
                            <span>
                              <PrimaryButton size="small" startIcon={<DownloadIcon />} onClick={() => handleDownload(a)}>
                                PDF
                              </PrimaryButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Snackbar
        open={toast.open}
        autoHideDuration={2600}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setToast((t) => ({ ...t, open: false }))} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Agreements;




